import React, { createContext, FC, useContext, useEffect, useState } from "react"
import { useHistory } from "react-router"
import { CourseListFilters, useGetInterestListsPublicQuery } from "../../generated/graphql"
import { margin } from "../../styles/layout"
import { InCommingAttributes } from "../../types/filters"
import { safeStorage } from "../../helpers/safeStorage"
import ForgotPassword from "../../views/ForgotPassword/ForgotPassword"
import LoginError from "../../views/LoginError/LoginError"
import LoginForm from "../../views/LoginForm/LoginForm"
import CourseFilters from "./CourseFilters"
import CoursesTable from "./CoursesTable"
import { CourseFilterContext } from "./CourseFilterContext"
import { AppSettings } from "../../layouts/Main/MainLayout"
import { resolveListType, showInterestLists, showRegularCourses } from "../interest/listType"
import { filterInterestLists } from "../interest/interestListFilter"

type CourseTableContainerProps = {
    incommingFilters: InCommingAttributes
    otherSettings: Partial<InCommingAttributes>
    appSettings: AppSettings
}

export type SpecialFilters = {
    instructorId?: string
    dayFilter?: string
    courseCategoryId?: string
    showOnlyInWebActive?: boolean
}

// We define our type for the context properties right here
type ContextProps = {
    visible: boolean
    record: any
    handleCancel: () => void
    forgotPassword: () => void
    loginForm: () => void
    handleOpen: (record: any) => void
    showError: (message: string) => void
}

// we initialise them without default values, to make that happen, we
// apply the Partial helper type.
export const CoursesContext = createContext<Partial<ContextProps>>({})

const CourseTableContainer: FC<CourseTableContainerProps> = ({ incommingFilters, otherSettings, appSettings }) => {
    // Filter-State aus dem zentralen Context (Single Source of Truth über dem
    // Router-Switch). Fallback auf internen State, damit der Container ohne
    // Provider (z.B. im Integrationstest) weiterhin eigenständig funktioniert.
    const courseFilterCtx = useContext(CourseFilterContext)
    const [internalFilters, setInternalFilters] = React.useState<CourseListFilters>({})
    const courseFilters = courseFilterCtx?.courseFilters ?? internalFilters
    const setCourseFilters = courseFilterCtx?.setCourseFilters ?? setInternalFilters
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [selectedId, setSelectedId] = React.useState<string | null>(null)

    // Ein expliziter listType-URL-Parameter hat Vorrang. Nur wenn er vollständig
    // fehlt, entscheidet der mandantenbezogene Web-Modul-Standard.
    const listType = resolveListType(
        otherSettings.listType as string | null | undefined,
        appSettings.showInterestListsByDefault
    )

    // Interessenten-Listen (client-seitig nach denselben Kurs-Filtern gefiltert),
    // werden als Zeilen ans Ende der Kurstabelle gemischt.
    const { data: interestData } = useGetInterestListsPublicQuery({
        fetchPolicy: "cache-and-network",
        skip: !showInterestLists(listType),
    })
    const interestLists = filterInterestLists(interestData?.interestListsPublic ?? [], {
        courseTypeId: courseFilters?.courseTypeId,
        courseTypeIds: courseFilters?.courseTypeIds,
        courseCategoryId: courseFilters?.courseCategoryId,
        courseCategoryIds: courseFilters?.courseCategoryIds,
        locationId: courseFilters?.locationId,
        city: courseFilters?.city,
        dayFilter: courseFilters?.dayFilter ?? undefined,
    })

    // AUTHENTICATION ---------------------------------------------------------------------------------------------------------
    const isLoggedIn = !!safeStorage.getItem("loggedInUser")
    const history = useHistory()
    const [isModalVisible, setIsModalVisible] = useState(false)
    const [isForgotModal, setIsForgotModal] = useState(false)
    const [isErrorModal, setIsErrorModal] = useState(false)
    const [isLoginForm, setIsLoginForm] = useState(true)
    const [forceReload, setForceReload] = useState(false)
    const [record, setRecord] = useState({})
    const [errorMessage, setErrorMessage] = useState("")

    useEffect(() => {
        const { location } = history
        if (location.state) {
            // @ts-ignore
            const { openLoginModal, reload } = location.state
            // @ts-ignore
            if (!isLoggedIn && openLoginModal) {
                if (openLoginModal) {
                    setIsModalVisible(true)
                    loginForm()
                }
            }
            // @ts-ignore
            if (reload) {
                setForceReload(true)
            }
        }
    }, [isLoggedIn, history])

    const handleCancel = () => {
        setIsModalVisible(false)
        setIsForgotModal(false)
        setIsLoginForm(true)
        setIsErrorModal(false)
    }
    const handleOpen = (data: any) => {
        setRecord(data)
        setIsModalVisible(true)
    }
    const forgotPassword = () => {
        setIsForgotModal(true)
        setIsLoginForm(false)
        setIsErrorModal(false)
    }
    const loginForm = () => {
        setIsForgotModal(false)
        setIsLoginForm(true)
        setIsErrorModal(false)
    }
    const showError = (message: React.SetStateAction<string>) => {
        setIsForgotModal(false)
        setIsLoginForm(false)
        setIsErrorModal(true)
        setErrorMessage(message)
    }

    // RETURN ------------------------------------------------------------------------------------------------------------------

    // console.group("CourseTableContainer")
    // console.log("courseListFilters", courseListFilters)
    // console.log("incommingFilters", incommingFilters)
    // console.groupEnd()

    return (
        <div id='courseTableContainer' /* style={{ background: "red" }} */>
            <CoursesContext.Provider
                value={{
                    visible: isModalVisible,
                    record: record,
                    handleCancel: handleCancel,
                    handleOpen: handleOpen,
                    forgotPassword: forgotPassword,
                    loginForm: loginForm,
                    showError: showError,
                }}
            >
                <CourseFilters
                    otherSettings={otherSettings}
                    courseFilters={courseFilters}
                    setCourseFilters={setCourseFilters}
                    incommingFilters={incommingFilters}
                    interestOnly={listType === "interest"}
                    style={{ marginBottom: margin.s, marginTop: margin.s }}
                />
                <CoursesTable
                    data-testid='course-table'
                    courseListFilters={courseFilters}
                    selectedId={(courseId: React.SetStateAction<string | null>) => setSelectedId(courseId)}
                    forceReload={forceReload}
                    appSettings={appSettings}
                    showCourseRows={showRegularCourses(listType)}
                    interestLists={showInterestLists(listType) ? interestLists : []}
                />
                {isLoginForm && <LoginForm />}
                {isForgotModal && <ForgotPassword />}
                {isErrorModal && <LoginError message={errorMessage} />}
            </CoursesContext.Provider>
        </div>
    )
}

export default CourseTableContainer
